import { CalendarClock } from 'lucide-react';

/**
 * Explains an empty "Year to Date" view after a school turns over its year, and
 * offers last year's numbers as a way out (ENG-9539, ENG-9580).
 *
 * Shrink wrap re-anchors the YTD start to today, so a returning site's YTD
 * reports are legitimately empty straight afterwards. The leader has no idea
 * that window just reset, so a bare "no results" reads as data loss. Callers
 * decide when to render this via `useYearToDateEmptyState`, which covers both
 * the scheduled-anchor case and the far more common resolved-empty one.
 *
 * The date is deliberately NOT named here — it's already on the period picker
 * for anyone who wants it, and repeating it invited "starts today", which reads
 * as a bug.
 *
 * Two variants for two kinds of surface:
 *
 *   - `empty`  — reports, where the window IS the content. Replaces the table /
 *                chart, mirroring the app's empty-state convention (soft icon
 *                circle over a bold heading and muted subtitle).
 *   - `banner` — grid-like surfaces (the points bubbles), where 0 is a valid
 *                reading and the page still has work to do. Sits above the
 *                content as one inline strip, never per cell.
 *
 * Omit `onShowLastYear` on surfaces with no time-period picker to target
 * (Meetup Prevention's sibling panels, the House Points shoutouts panel): the
 * explanation still renders, without a dead-end call to action.
 */
export interface YearToDateNotStartedProps {
  variant: 'empty' | 'banner';
  /**
   * Applies `lastYearRange` as an explicit Custom range. Per-surface, because
   * each one owns its own period handler. Omit to render without a CTA.
   */
  onShowLastYear?: () => void;
  className?: string;
}


export function YearToDateNotStarted({
  variant,
  onShowLastYear,
  className,
}: YearToDateNotStartedProps) {
  if (variant === 'banner') {
    return (
      // role="status" so the explanation is announced when it replaces the
      // loading state — without it, a screen reader user sees only zeroes.
      <div
        role="status"
        className={`flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-primary-200 bg-primary-50 px-4 py-3 ${className ?? ''}`}
      >
        <CalendarClock className="h-5 w-5 shrink-0 text-primary-600" aria-hidden="true" />
        <p className="flex-1 text-sm text-gray-700">
          Showing 0 for the new school year.
        </p>
        {onShowLastYear && (
          <button
            type="button"
            onClick={onShowLastYear}
            className="rounded text-sm font-semibold text-primary-600 underline decoration-primary-300 underline-offset-2 transition-colors hover:text-primary-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
          >
            See last year&rsquo;s numbers
          </button>
        )}
      </div>
    );
  }

  return (
    <div
      className={`flex min-h-[55vh] flex-col items-center justify-center py-16 text-center ${className ?? ''}`}
    >
      <span className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-secondary-50">
        <CalendarClock className="h-10 w-10 text-secondary-600" aria-hidden="true" />
      </span>
      <h2 className="mt-5 text-2xl font-bold tracking-tight text-gray-900">
        No data yet for the new school year
      </h2>
      <p className="mt-2 max-w-sm text-sm text-gray-500">
        Points you give from here will show up in this view.
        {onShowLastYear ? " Want last year's numbers instead?" : ''}
      </p>
      {onShowLastYear && (
        <button
          type="button"
          onClick={onShowLastYear}
          className="mt-5 rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
        >
          View last year
        </button>
      )}
    </div>
  );
}
